import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/ProteinComponent.css";
import RightSidebar from "./RightSidebar";


const sanitizeId = (s = "") => String(s).replace(/[^a-zA-Z0-9_-]/g, "_");

const computeAAlengthFromFasta = (fasta) => {
  if (!fasta) return 0;
  const lines = fasta.split(/\r?\n/);
  const seq = lines.filter((l) => !l.startsWith(">")).join("").replace(/[^A-Za-z]/g, "");
  return seq.length;
};

const ProteinComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlProtein = params.get("protein") || null;

  const [proteins, setProteins] = useState([]);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [searchTerm, setSearchTerm] = useState(params.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFasta, setExpandedFasta] = useState(null);
  const [expandedGenPept, setExpandedGenPept] = useState(urlProtein);
  const [genPeptData, setGenPeptData] = useState({});
  const [loadingGenPept, setLoadingGenPept] = useState(false);
  const [selectedProteins, setSelectedProteins] = useState(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [copiedMap, setCopiedMap] = useState({});
  const [isResultOnly, setIsResultOnly] = useState(!!urlProtein);
  

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Sync URL when search or page changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (currentPage > 1) params.set("page", currentPage);
    if (expandedGenPept) params.set("protein", expandedGenPept);
    navigate({ search: params.toString() }, { replace: true });
  }, [searchTerm, currentPage, expandedGenPept, navigate]);

  // Fetch Proteins from API
  useEffect(() => {
    const fetchProteins = async () => {
      setLoading(true);
      try {
        const baseUrl = "http://197.156.76.122/api/protein/";
        const params = new URLSearchParams();
        params.append("page", currentPage);
        params.append("page_size", pageSize);
        if (debouncedSearch) params.append("search", debouncedSearch);

        const response = await axios.get(`${baseUrl}?${params.toString()}`);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];

        setProteins(data);
        setTotalCount(response.data.count || data.length || 0);
        setError(null);
      } catch (err) {
        console.error("Protein fetch error:", err);
        setError("❌ Failed to fetch protein data. Please try again later.");
        setProteins([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchProteins();
  }, [currentPage, pageSize, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const getAvailableFormats = (protein) => {
  const formats = [];

  if (protein.fasta) formats.push("FASTA");
  if (protein.genpept) formats.push("GenPept");
  if (protein.xml) formats.push("XML");
  if (protein.json) formats.push("JSON");

  // ONLY trust DB-provided formats (not hardcoded guessing)
  if (Array.isArray(protein.available_formats)) {
    formats.push(...protein.available_formats);
  }

  return [...new Set(formats)];
};

const renderPagination = () => (
  <div className="pagination-controls">
    <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
      ⏮
    </button>

    <button
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
    >
      ◀
    </button>

    <span>
      Page {currentPage} / {totalPages} | Total: {totalCount} proteins
    </span>

    <button
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
    >
      ▶
    </button>

    <button
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage(totalPages)}
    >
      ⏭
    </button>
  </div>
);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const toggleFasta = (accession) =>
    setExpandedFasta((prev) => (prev === accession ? null : accession));

  const toggleGenPept = (accession) => {
	  setExpandedGenPept(accession);
	  setIsResultOnly(true);
	};

  const copyFasta = (fasta, accession) => {
    if (!fasta) return;
    try {
      navigator.clipboard.writeText(fasta);
      const safe = sanitizeId(accession);
      setCopiedMap((prev) => ({ ...prev, [safe]: true }));
      setTimeout(() => setCopiedMap((prev) => ({ ...prev, [safe]: false })), 1500);
    } catch (err) {
      alert("Copy failed: " + err.message);
    }
  };

  const handleCheckboxChange = (accession) => {
    setSelectedProteins((prev) => {
      const updated = new Set(prev);
      if (updated.has(accession)) updated.delete(accession);
      else updated.add(accession);
      return updated;
    });
  };

  const handleDownloadFormat = async (format) => {
    if (!selectedProteins.size) {
      alert("No proteins selected.");
      return;
    }

    const proteinsArray = proteins.filter((p) => selectedProteins.has(p.accession));

    switch (format) {
      case "fasta": {
        const content = proteinsArray
          .map((p) => p.fasta || `>No sequence for ${p.accession}`)
          .join("\n");
        downloadFile(content, "proteins.fasta", "text/plain");
        break;
      }
      case "genpept": {
        const firstAccession = proteinsArray[0].accession;
        toggleGenPept(firstAccession);
        break;
      }
      case "json": {
        const content = JSON.stringify(proteinsArray, null, 2);
        downloadFile(content, "proteins.json", "application/json");
        break;
      }
      case "xml": {
        const xmlContent = `<proteins>\n${proteinsArray
          .map(
            (p) =>
              `<protein><accession>${p.accession}</accession><title>${p.title}</title></protein>`
          )
          .join("\n")}\n</proteins>`;
        downloadFile(xmlContent, "proteins.xml", "application/xml");
        break;
      }
      default:
        break;
    }

    document.getElementById("download-format").value = "";
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // GenPept result-only view
  

// GenPept result-only view
const selectedProtein = proteins.find(
  (p) => (p.accession || p.id) === expandedGenPept
);

if (isResultOnly && expandedGenPept) {
  return (
    <div className="protein-page">
      <div className="protein-main">
        <div className="genpept-header">
          <button
            className="back-button"
            onClick={() => {
              setExpandedGenPept(null);
              setIsResultOnly(false);
            }}
          >
            🔙 Back to List
          </button>

          <h2 className="header2">
            GenPept View ({expandedGenPept})
          </h2>
        </div>

        <div className="genpept-container">
          <div className="genpept-header">
            <button
              className="copy-button"
              onClick={() => {
                navigator.clipboard.writeText(
                  selectedProtein?.genpept || ""
                );
                alert("✅ GenPept copied to clipboard!");
              }}
            >
              📋 Copy GenPept
            </button>
          </div>

          {!selectedProtein ? (
            <p>Loading protein data...</p>
          ) : (
            <pre className="genpept-box">
              {selectedProtein.genpept || "No GenPept data available"}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}


  // Default list view
  return (
    <div className="protein-page">
      <div className="protein-main">
        <div className="header-row">
          <h2 className="header2">Protein Data</h2>

          <div className="download-top-right">
		  <span className="download-label">⬇ Download:</span>

		  <select
		    id="download-format"
		    onChange={(e) => handleDownloadFormat(e.target.value)}
		    value=""
		    className={selectedProteins.size ? "download-enabled" : "download-disabled"}
		    disabled={!selectedProteins.size}
		  >
		    <option value="" disabled>
		      Select format
		    </option>

		    {selectedProteins.size && (
		      <>
			<option value="fasta">FASTA</option>
			<option value="genpept">GenPept</option>
			<option value="json">JSON</option>
			<option value="xml">XML</option>
		      </>
		    )}
		  </select>
	   </div>
        </div>

        <div className="search-container">
	  <input
	    placeholder="🔍 Search by accession or title"
	    value={searchTerm}
	    onChange={handleSearchChange}
	    className="search-input-large"
	  />
	</div>

	{renderPagination()}

        {loading && <p>Loading...</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !proteins.length && <p className="no-data">No protein data found.</p>}

        {!loading && !error && proteins.length > 0 && (
          <div className="protein-list">
            {proteins.map((protein, index) => {
              const accession = protein.accession || protein.id || `unknown_${index}`;
              const seqLen =
                protein.sequence_length || computeAAlengthFromFasta(protein.fasta);

              const ncbiLinks = {
                Taxonomy: protein.tax_id
                  ? `https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${protein.tax_id}`
                  : null,
                Nucleotide: protein.nucleotide_accession
                  ? `https://www.ncbi.nlm.nih.gov/nuccore/${protein.nucleotide_accession}`
                  : null,
                PubMed: protein.pubmed_id
                  ? `https://pubmed.ncbi.nlm.nih.gov/${protein.pubmed_id}`
                  : null,
                UniProt: protein.uniprot_accession
                  ? `https://www.uniprot.org/uniprotkb/${protein.uniprot_accession}/entry`
                  : null,
              
	    };
	    const getNcbiProteinUrl = (protein) => {
		  if (protein?.accession) {
		    return `https://www.ncbi.nlm.nih.gov/protein/${protein.accession}`;
		  }

		  if (protein?.gi) {
		    // fallback legacy-safe path
		    return `https://www.ncbi.nlm.nih.gov/nuccore/${protein.gi}`;
		  }

		  return null;
		};
	
              return (
                <div className="protein-card" key={accession}>
                  <div className="card-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedProteins.has(accession)}
                        onChange={() => handleCheckboxChange(accession)}
                        className="protein-checkbox"
                      />
                      <span
                        className="protein-title clickable"
                        onClick={() => toggleGenPept(accession)}
                      >
                        <u>{protein.title || "Untitled Protein"}</u>
                      </span>
                    </label>
                  </div>

                  <div className="description-block">
                    <p>
                      {(currentPage - 1) * pageSize + index + 1}. <strong>{seqLen}</strong> aa protein
                    </p>
                    <p>
                      <strong>Accession:</strong>{" "}
                      <span
                        className="accession clickable"
                        onClick={() => toggleGenPept(accession)}
                      >
                        {accession}
                      </span>
                      {protein.gi && (
			  <>
			    {" | "}
			    <strong>GI:</strong>{" "}
			    <a
			      href={`https://www.ncbi.nlm.nih.gov/protein/${protein.accession}`}
			      target="_blank"
			      rel="noopener noreferrer"
			      className="gi-link"
			    >
			      {protein.gi}
			    </a>
			  </>
			)}
                    </p>
                    
                   
                    {/* Inline metadata links + FASTA + GenPept */}
                    <div className="metadata-links">
                      {Object.entries(ncbiLinks)
                        .filter(([_, v]) => v)
                        .map(([key, link], i, arr) => (
                          <React.Fragment key={key}>
                            <a href={link} target="_blank" rel="noopener noreferrer">
                              {key}
                            </a>
                            {i < arr.length - 1 && " | "}
                          </React.Fragment>
                        ))}

                      {protein.fasta && (
                        <>
                          {" | "}
                          <button
                            className="inline-link-button"
                            onClick={() => toggleFasta(accession)}
                          >
                            {expandedFasta === accession ? "Hide FASTA" : "FASTA"}
                          </button>
                        </>
                      )}

                      {" | "}
                      <button
                        className="inline-link-button"
                        onClick={() => toggleGenPept(accession)}
                      >
                        GenPept
                      </button>
                    </div>

                    {/* Expanded FASTA */}
                    {expandedFasta === accession && protein.fasta && (
                      <div className="fasta-box">
                        <button onClick={() => copyFasta(protein.fasta, accession)}>
                          📋 Copy FASTA
                        </button>
                        {copiedMap[sanitizeId(accession)] && (
                          <span className="copied-indicator"> ✅ Copied!</span>
                        )}
                        <pre>{protein.fasta}</pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {proteins.length > 0 && renderPagination()}
      </div>

      <RightSidebar />
    </div>
  );
};

export default ProteinComponent;

